import { LitElement, html, css, ref, createRef } from "@lit";
import {
  computePosition,
  flip,
  shift,
  offset,
} from "https://cdn.jsdelivr.net/npm/@floating-ui/dom@1.8.0/+esm";

class VariantTooltip extends LitElement {
  tooltipRef = createRef();

  static properties = {
    content: {
      type: String,
      attribute: "content",
    },
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.firstElementChild) return;
    this.trigger = this.firstElementChild;

    if (this.getAttribute("type") === "description") {
      this.trigger.setAttribute(
        "aria-description",
        this.getAttribute("text") || "",
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearTapTimers();
    this.tooltip?.togglePopover?.(false);
    this.tooltip?.remove();

    if (typeof this.removeEventHandlers === "function") {
      this.removeEventHandlers();
    }
  }

  clearTapTimers() {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    this.showTimer = null;
    this.hideTimer = null;
  }

  firstUpdated() {
    this.tooltip = this.tooltipRef.value;
    this.removeEventHandlers = this.setupEventHandlers();
  }

  setupEventHandlers() {
    function hovered() {
      this.hovered = true;
      this.show();
    }

    function unhovered() {
      this.hovered = false;
      this.hide();
    }

    function focused() {
      this.focused = true;
      this.show();
    }

    function unfocused() {
      this.focused = false;
      this.hide();
    }

    function handleKeydown({ key, ctrlKey }) {
      if (key === "Escape" || ctrlKey) {
        this.tooltip.togglePopover?.(false);
      }
    }

    if (matchMedia("(hover: hover)").matches) {
      this.trigger.addEventListener("pointerenter", hovered.bind(this));
      this.trigger.addEventListener("pointerleave", unhovered.bind(this));
      this.trigger.addEventListener("focus", focused.bind(this));
      this.trigger.addEventListener("blur", unfocused.bind(this));
    }

    this.tooltip.addEventListener("pointerenter", hovered.bind(this));
    this.tooltip.addEventListener("pointerleave", unhovered.bind(this));

    this.ownerDocument.addEventListener("keydown", handleKeydown.bind(this));

    function pointerdown() {
      this.clearTapTimers();
      this.tapping = true;
      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        this.show();
      }, 200);
    }

    function pointerup() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.hideTimer = null;
        this.tapping = false;
        this.hide();
      }, 2000);
    }

    this.trigger.addEventListener("pointerleave", pointerup.bind(this));
    this.trigger.addEventListener("touchstart", pointerdown.bind(this));

    return function () {
      if (matchMedia("(hover: hover)").matches) {
        this.trigger.removeEventListener("pointerenter", hovered.bind(this));
        this.trigger.removeEventListener("pointerleave", unhovered.bind(this));
        this.trigger.removeEventListener("focus", focused.bind(this));
        this.trigger.removeEventListener("blur", unfocused.bind(this));
      }

      this.tooltip.removeEventListener("pointerenter", hovered.bind(this));
      this.tooltip.removeEventListener("pointerleave", unhovered.bind(this));

      this.ownerDocument.removeEventListener(
        "keydown",
        handleKeydown.bind(this),
      );

      this.trigger.removeEventListener("pointerleave", pointerup.bind(this));
      this.trigger.removeEventListener("touchstart", pointerdown.bind(this));
    };
  }

  async show() {
    await new Promise((resolve) => setTimeout(resolve, 100));

    if (!this.hovered && !this.focused && !this.tapping) return;
    this.tooltip.togglePopover?.(true);

    const { x, y } = await computePosition(this.trigger, this.tooltip, {
      placement: "top",
      middleware: [offset(16), flip(), shift({ padding: 24 })],
    });

    this.tooltip.style.transform = `translate(${x}px,${y}px)`;
  }

  async hide() {
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (this.focused || this.hovered || this.tapping) return;
    this.tooltip.togglePopover?.(false);
  }

  render() {
    return html`
      <slot></slot>
      <span
        ${ref(this.tooltipRef)}
        name="content"
        part="content"
        class="tooltip"
        popover="manual"
        role="tooltip"
      >
        ${this.content}
      </span>
    `;
  }

  static styles = css`
    .tooltip {
      box-sizing: border-box;
      inset: 0;
      margin-block: 0;
      margin-inline: 12px;
      overflow: visible;
      display: block;
      position: absolute;
      border: 1px solid var(--nova-color-grey-300);
      border-radius: var(--nova-border-radius-xs);
      box-shadow: 0 0 1px rgba(0, 0, 0, 0.2);
      color: var(--nova-color-grey-600);
      padding: var(--nova-space-xs);
      background: var(--nova-color-white);
      max-inline-size: min(90vi, 30ch);
      z-index: 5;
    }

    .tooltip:not(:popover-open) {
      display: none;
    }
  `;
}

customElements.define("custom-variant-tooltip", VariantTooltip);
